import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule, MatFormFieldAppearance } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Subject, takeUntil } from 'rxjs';
import { Recommendation } from '../../dto/recommendation';
import { BoolDto } from '../../../../common/dto/bool-dto';
import { RecommendationPropertiesService } from '../../service/recommendation-properties.service';
import { RecommendationType } from '../../dto/recommendation-type';
import { RecommendationService } from '../../service/recommendation.service';
import { Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../../../common/component/dialog/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'dpi-recommendation-detail',
  imports: [FormsModule, MatFormFieldModule, MatInputModule, MatCardModule, MatIconModule, MatButtonModule, MatToolbarModule,
    MatTooltipModule, MatSelectModule, MatDialogModule, NgIf],
  templateUrl: './recommendation-detail.component.html',
  styleUrl: './recommendation-detail.component.scss'
})
export class RecommendationDetailComponent implements OnInit, OnDestroy {
  fieldAppearance: MatFormFieldAppearance;
  destroyed$ = new Subject<void>(); // notifier of component being destroyed
  isReadonly: boolean;
  isContentChanged: boolean;
  private _dialogConfirm = inject(MatDialog);

  recommendation: Recommendation;

  recommedationTypes: RecommendationType[];
  selectedRecommendationType: RecommendationType;

  closedStates: BoolDto[];
  selectedCloseState: BoolDto;

  constructor(private recommendationService: RecommendationService
    , private recommendationPropertiesService: RecommendationPropertiesService
    , private notifyService: StateNotifyService
    , private router: Router) {
    this.isReadonly = true;
    this.fieldAppearance = 'outline';
    this.isContentChanged = false;

    this.recommendation = this.notifyService.getSelectedRecommendation();

    this.recommedationTypes = this.recommendationPropertiesService.getAllRecommendationTypes();
    this.selectedRecommendationType = this.recommendationPropertiesService.getRecommendationType(this.recommedationTypes, this.recommendation.type);

    this.closedStates = this.recommendationPropertiesService.getAllRecommendationClosedStates();
    this.selectedCloseState = this.recommendationPropertiesService.getRecommendationClosedState(this.closedStates, this.recommendation.closed);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.recommendationSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.recommendation = model;
        this.selectedRecommendationType = this.recommendationPropertiesService.getRecommendationType(this.recommedationTypes, this.recommendation.type);
        this.selectedCloseState = this.recommendationPropertiesService.getRecommendationClosedState(this.closedStates, this.recommendation.closed);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.onEditModeChange(res);
      });
  }

  onEditModeChange(isReadonly: boolean) {
    this.isReadonly = isReadonly;
    if (isReadonly) {
      this.fieldAppearance = 'outline';
    } else {
      this.fieldAppearance = 'fill';
    }
  }

  onContentChange() {
    this.isContentChanged = true;
  }

  recommendationTypeChanged() {
    this.recommendation.type = this.selectedRecommendationType.id;
    this.isContentChanged = true;
  }

  closeStateChanged() {
    this.recommendation.closed = this.selectedCloseState.state;
    this.isContentChanged = true;
  }

  // --- CRUD actions ---

  createRecommendation() {
    this.recommendationService.createRecommendation(this.recommendation.test_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addModelToSytem(model);
        },
        error: err => { console.log(err); }
      });
  }

  updateRecommendation() {
    this.isContentChanged = false; // Reset the state, and disable the button to prevent multiple clicks
    this.recommendationService.updateRecommendation(this.recommendation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.recommendation = model;
          this.notifyService.updateRecommendationSelection(model);
        },
        error: err => { console.log(err); }
      });
  }

  deleteRecommendation() {
    this.recommendationService.deleteRecommendation(this.recommendation.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeRecommendationAfterDelete();
        },
        error: err => { console.log(err); }
      });
  }

  private addModelToSytem(model: Recommendation) {
    this.recommendation = model;
    this.notifyService.updateRecommendationSelection(this.recommendation);
    let listRecommendation = this.notifyService.getListAllRecommendation();
    listRecommendation.push(model);
    this.notifyService.updateListAllRecommendation(listRecommendation);
  }

  private removeRecommendationAfterDelete() {
    let listRecommendation = this.notifyService.getListAllRecommendation();
    const index = listRecommendation.indexOf(this.recommendation, 0);
    if (index > -1) {
      listRecommendation.splice(index, 1);
    }
    this.notifyService.updateListAllRecommendation(listRecommendation);

    // Select the current 'test', then navigate to 'test-hub', i.e. to load and display the list of recommendations after deletion.
    let testModel = this.notifyService.getSelectedTest();
    this.notifyService.updateTestSelection(testModel); //update the same test selection, so that adaption of display features in 'app.component.ts' can be triggered. 
    this.router.navigate(['test-hub']);
  }

  btnDeleteClicked() {
    if (!this.isReadonly) {
      let message = "The whole recommendation will be deleted, sure to proceed?"
      const dialogRef = this._dialogConfirm.open(ConfirmDialogComponent, { data: message });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.confirmed) {
              this.deleteRecommendation();
            }
          };
        });
    }
  }


}
